/**
 * AppearanceSettings
 * Settings → General "Appearance" section: System / Light / Dark segmented control.
 * Applies instantly (no Save button); the choice is saved on this device only.
 */

import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/theme.context';
import type { ThemePreference } from '@/lib/theme';

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function AppearanceSettings() {
  const { preference, systemTheme, setPreference } = useTheme();

  const helper =
    preference === 'system'
      ? `System follows your device setting, currently ${systemTheme}. Your choice is saved on this device only.`
      : `${preference === 'dark' ? 'Dark' : 'Light'} is always used on this device. Choose System to follow your device setting.`;

  return (
    <section aria-labelledby="appearance-heading" data-testid="settings-appearance">
      <h2 id="appearance-heading" className="mb-1 text-lg font-semibold text-foreground">
        Appearance
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">Choose how Resida looks on this device.</p>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-[var(--card-padding)]">
        <fieldset
          data-testid="theme-preference"
          className="m-0 flex w-full gap-1 rounded-[10px] border-0 bg-muted p-1 md:w-fit"
        >
          <legend className="sr-only">Theme</legend>
          {OPTIONS.map(({ value, label }) => {
            const selected = preference === value;
            return (
              <label
                key={value}
                className={cn(
                  'relative flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-[7px] px-5 text-sm md:flex-none',
                  'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring',
                  selected
                    ? 'bg-card font-semibold text-foreground shadow-[0_0_0_1px_hsl(var(--border))]'
                    : 'text-muted-foreground',
                )}
              >
                <input
                  type="radio"
                  name="theme"
                  value={value}
                  checked={selected}
                  onChange={() => setPreference(value)}
                  data-testid={`theme-option-${value}`}
                  className="absolute inset-0 m-0 cursor-pointer opacity-0"
                />
                {label}
              </label>
            );
          })}
        </fieldset>
        <p className="text-[13px] text-muted-foreground" data-testid="theme-helper">
          {helper}
        </p>
      </div>
    </section>
  );
}
